import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ReferenceDownloadFAQ = () => {
  const sections = [
    {
      heading: "General Overview",
      faqs: [
        {
          question: "Q: What is the Reference Downloader module?",
          answer:
            "A: A high-speed retrieval tool in JUNE that allows users to bulk-download patent references and associated metadata in a single organized ZIP package for IDS and USPTO filing preparation."
        },
        {
          question:
            "Q: Can I use this feature to download references from any country?",
          answer:
            "A: The feature supports patent document retrieval from all countries globally, subject to availability from the source patent offices."
        },
        {
          question: "Q: How do I format my search request?",
          answer:
            "A: Enter references using the standard format: Country Code+Publication Number+Kind Code (e.g., US10123456B2)"
        },
        {
          question: "Q: Is there a limit on how many I can download?",
          answer:
            "A: You can submit up to 100 references per request. For larger IDS filings, multiple requests can be submitted sequentially."
        }
      ]
    },
    {
      heading: "Automation & Delivery",
      faqs: [
        {
          question: "Q: How long does it take to get my references?",
          answer: `A: If references are already in the JUNE library, delivery is immediate. 
If the document must be retrieved from external sources or requires additional processing (like foreign publications), the system follows automated service windows with real-time status updates.`
        },
        {
          question: "Q: How are delivery timelines determined for my request?",
          answer: (
            <>
              <p>
                <strong>A:</strong> Delivery timelines are automatically determined based on the number of references submitted and the time of request. The standard service windows are as follows:
              </p>

              <br />

              <p><strong>Up to 25 references per request:</strong></p>
              <p>a) Submitted before 12 PM (EST): ~4 hours</p>
              <p>b) Submitted after 12 PM (EST): ~16 hours</p>

              <br />

              <p><strong>26+ references per request:</strong></p>
              <p>Submitted at any time: ~18 hours</p>
            </>
          )
        },
        {
          question:
            "Q: What does an ‘SLA Update’ or ‘Delayed’ status mean?",
          answer:
            "A: This indicates the system is processing a complex request that requires more time due to large file sizes, high system load, or the need to retrieve documents from external patent office databases."
        },
        {
          question: "Q: Are the files ready for the USPTO?",
          answer:
            "A: Yes. Documents are automatically processed to ensure USPTO compatibility, including embedded font removal and file size optimization (maximum 25 MB per document)."
        }
      ]
    },
    {
      heading: "English vs Foreign Reference Handling",
      faqs: [
        {
          question:
            "Q: How are English and non-English references handled?",
          answer: `A: Depending on availability from the source, JUNE prepares the reference in one of the following formats to support IDS preparation:
• Format 1: Original document merged with its English abstract, where an abstract is available.
• Format 2: Original document merged with English translations of claims, description, and key bibliographic data, where translation content is available.`
        },
        {
          question: "Q: What is included in the download package?",
          answer: `A: Every download includes:
• Reference PDFs ready for USPTO submission (original documents processed to remove embedded fonts and comply with USPTO file requirements).
• Metadata file with key bibliographic information for all references in the request`
        },
        {
          question:
            "Q: What specific data is in the metadata file?",
          answer:
            "A: The metadata file provides a summary of the batch, including reference numbers, publication dates, applicant and inventor names, and indicators for abstract or translation availability."
        },
        {
          question:
            "Q: Why is some information (like names) in a foreign language?",
          answer:
            "A: This is not an error; the system pulls data exactly as it appears at the original source to ensure accuracy."
        }
      ]
    },
    {
      heading: "Interpreting the Observations Report",
      faqs: [
        {
          question:
            'Q: How do I fix an "Invalid format" error in the report?',
          answer:
            "A: Ensure each reference follows the required format: Country Code+Pub/Pat Number+Kind Code."
        },
        {
          question:
            'Q: What if a reference is marked as "Not Found" in the report?',
          answer:
            "A: This means the specific information or the PDF could not be retrieved from the primary source databases."
        },
        {
          question:
            'Q: Does "Abstract- Not applicable" mean the download failed?',
          answer:
            "A: No. This message simply means that for that specific jurisdiction or publication type, an abstract is not a formal requirement. Your original document and metadata are still processed where available."
        }
      ]
    },
    {
      heading: "Troubleshooting",
      faqs: [
        {
          question:
            "Q: How do I know if a reference failed?",
          answer:
            "A: JUNE provides a Processing Observations Report. The report highlights references that were incorrectly formatted or unavailable at the source."
        },
        {
          question:
            "Q: Who do I contact for technical support?",
          answer:
            "A: Please reach out to the JUNE Support team at product@trexoglobal.com"
        },
        {
          question:
            "Q: What information should I provide for support?",
          answer:
            "A: To help us resolve your query quickly, please include your Request ID, the reference numbers used, a description of the issue, and screenshots if available."
        }
      ]
    }
  ];

  return (
    <Box sx={{ p: 2 }}>

      {sections.map((section, sIndex) => (
        <Box key={sIndex} sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 1, color: '#1976d2' }}
          >
            {section.heading}
          </Typography>

          {section.faqs.map((item, index) => (
            <Accordion
              key={index}
              defaultExpanded={sIndex === 0 && index === 0}
              sx={{
                backgroundColor: "#fff",
                "&.Mui-expanded": {
                  margin: 0
                },
                borderBottom: "1px solid #e0e0e0"
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" fontWeight="bold">
                  {item.question}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Typography sx={{ whiteSpace: "pre-line" }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default ReferenceDownloadFAQ;